import Loading from 'quasar/src/plugins/loading/Loading.js';import useQuasar from 'quasar/src/composables/use-quasar/use-quasar.js';
import logger from 'src/utils/logger';
import configCloud from '../../configCloud';
// Rate limiting for login attempts
const LOGIN_RATE_LIMIT = {
    attempts: 0,
    lastAttempt: 0,
    backoffMs: 1000,
    maxBackoffMs: 30000,
    resetAfterMs: 60000
};
const TOKEN_REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
class UserClass {
    $q;
    tokenRefreshTimer = null;
    constructor() {
        this.$q = useQuasar();
    }
    async openFreshWSConnection() {
        try {
            this.$q.class.WS.disconnect();
        }
        catch (e) {
            logger.debug('User auth: WS disconnect failed', e);
        }
        return await this.$q.class.WS.connect();
    }
    /**
     * Check and enforce rate limiting for login attempts
     * Returns delay in ms if rate limited, 0 if allowed
     */
    checkLoginRateLimit() {
        const now = Date.now();
        // Reset if enough time has passed since last attempt
        if (now - LOGIN_RATE_LIMIT.lastAttempt > LOGIN_RATE_LIMIT.resetAfterMs) {
            LOGIN_RATE_LIMIT.attempts = 0;
            LOGIN_RATE_LIMIT.backoffMs = 1000;
        }
        LOGIN_RATE_LIMIT.lastAttempt = now;
        LOGIN_RATE_LIMIT.attempts++;
        // First 3 attempts are free
        if (LOGIN_RATE_LIMIT.attempts <= 3) {
            return 0;
        }
        // Apply exponential backoff after 3 failed attempts
        const delay = LOGIN_RATE_LIMIT.backoffMs;
        LOGIN_RATE_LIMIT.backoffMs = Math.min(LOGIN_RATE_LIMIT.backoffMs * 2, LOGIN_RATE_LIMIT.maxBackoffMs);
        return delay;
    }
    /**
     * Reset rate limit on successful login
     */
    resetLoginRateLimit() {
        LOGIN_RATE_LIMIT.attempts = 0;
        LOGIN_RATE_LIMIT.backoffMs = 1000;
    }
    async reg(params) {
        try {
            Loading.show();
            const connection = await this.openFreshWSConnection();
            if (!connection) {
                this.$q.store.dialog.set({
                    show: true,
                    title: 'Error',
                    text: 'Failed to connect to server',
                    ok: { color: 'negative' }
                });
                return false;
            }
            const response = await this.$q.ws.sendRequest({
                type: 'query',
                iface: 'user',
                method: 'registration',
                args: {
                    login: params.login,
                    userName: params.userName,
                    password: params.password,
                    mail: params.mail,
                    phone: params.phone || '',
                    telegram_username: params.telegram_username || '',
                    cluster: configCloud.cluster
                }
            });
            if (response && response.args === 'done') {
                return true;
            }
            else if (response && response.args && response.args.message) {
                this.$q.store.dialog.set({
                    show: true,
                    title: 'Error',
                    text: response.args.message,
                    ok: { color: 'negative' }
                });
                return false;
            }
            return false;
        }
        catch (error) {
            logger.log('Registration error:', error);
            this.$q.store.dialog.set({
                show: true,
                title: 'Error',
                text: 'Registration failed. Please try again.',
                ok: { color: 'negative' }
            });
            return false;
        }
        finally {
            Loading.hide();
        }
    }
    async confirmReg(params) {
        const connection = await this.openFreshWSConnection();
        if (!connection)
            return;
        const response = await this.$q.ws.sendRequest({
            type: 'query',
            iface: 'user',
            method: 'confirmReg',
            args: {
                login: params.login,
                tokenConfirmation: params.token
            }
        });
        if (response.args.id) {
            const data = response.args;
            await this.onAuth(data);
        }
        else {
            throw new Error(response.args.message || 'Confirmation failed');
        }
    }
    /**
     * Логин с rate limiting
     */
    async login(params) {
        // Check rate limiting
        const delay = this.checkLoginRateLimit();
        if (delay > 0) {
            this.$q.store.dialog.set({
                show: true,
                title: 'Error',
                text: `Too many login attempts. Please wait ${Math.ceil(delay / 1000)} seconds.`,
                ok: {
                    color: 'negative'
                }
            });
            return false;
        }
        const connection = await this.openFreshWSConnection();
        if (!connection)
            return;
        const response = await this.$q.ws.sendRequest({
            type: 'query',
            iface: 'user',
            method: 'login',
            args: {
                login: params.login,
                password: params.password,
                cluster: configCloud.cluster
            }
        });
        if (response && response.args) {
            // Если рутовый, не требующий авторизации
            if (response.args.id) {
                const data = response.args;
                this.resetLoginRateLimit(); // Reset on success
                await this.onAuth(data);
                return 'ok';
            }
            // Если успешно отправлен код
            else if (response.args === 'done') {
                this.resetLoginRateLimit(); // Reset on success
                return true;
            }
            // Если не удалось отправить код
            else {
                this.$q.ws.removeAllListeners();
                // this.$q.ws.close();
                if (response.args.message) {
                    this.$q.store.dialog.set({
                        show: true,
                        title: 'Error',
                        text: response.args.message || 'Auth error',
                        ok: {
                            color: 'negative'
                        }
                    });
                }
                return false;
            }
        }
    }
    async verifyCode(params) {
        if (!this.$q.ws) {
            const connection = await this.$q.class.WS.connect();
            if (!connection)
                return;
        }
        const response = await this.$q.ws.sendRequest({
            type: 'query',
            iface: 'user',
            method: 'confirmLogin',
            args: {
                loginConfirmation: params.code
            }
        });
        // Если успешно отправлен код
        if (response.args.id) {
            const data = response.args;
            await this.onAuth(data);
        }
        // Если неуспешная верификация
        else {
            this.$q.store.dialog.set({
                show: true,
                title: 'Error',
                text: response.args.message || 'Auth error',
                ok: {
                    color: 'negative'
                }
            });
        }
    }
    /**
     * Login with Telegram Web App data
     */
    async loginWithTelegram(initData) {
        const connection = await this.openFreshWSConnection();
        if (!connection)
            return false;
        const response = await this.$q.ws.sendRequest({
            type: 'query',
            iface: 'user',
            method: 'loginWithTelegram',
            args: {
                initData,
                cluster: configCloud.cluster
            }
        });
        if (response && response.args) {
            if (response.args.id && response.args.token) {
                const data = response.args;
                this.resetLoginRateLimit();
                await this.onAuth(data);
                return true;
            }
        }
        return false;
    }
    /**
     * Авторизация по токену
     */
    async loginWithToken(token) {
        const response = await this.$q.ws.sendRequest({
            type: 'query',
            iface: 'user',
            method: 'loginWithToken',
            args: {
                token,
                cluster: configCloud.cluster
            }
        });
        // Если юзер авторизовался
        if (response && response.args) {
            if (response.args.id && response.args.token) {
                const data = response.args;
                await this.onAuth(data);
            }
        }
        // Если юзер не авторизовался
        else {
            this.$q.store.app.user = null;
            sessionStorage.removeItem('token');
            this.clearTokenRefreshTimer();
        }
    }
    /**
     * Действия после авторизации по логину или токену
     */
    async onAuth(data) {
        // Запись юзера в хранилище состояний
        const user = {
            active: data.active,
            group: data.group[0],
            id: data.id,
            is_deleted: data.is_deleted,
            login: data.login,
            mail: data.mail,
            phone: data.phone || '',
            telegram_username: data.telegram_username || '',
            token: data.token,
            token_expire: data.token_expire,
            userName: data.userName
        };
        this.$q.store.app.user = structuredClone(user);
        logger.log('user', user);
        // Запись токена в локальном хранилище
        sessionStorage.setItem('token', user.token);
        this.startTokenRefresh(user.token);
        // Запрос списка групп пользователей из базы данных (только для админов)
        if (user.group && user.group.id === 1) {
            const responseRoles = await this.$q.ws.sendRequest({
                type: 'query',
                iface: 'user',
                method: 'getGroupList',
            });
            if (responseRoles && responseRoles.args && responseRoles.args.rows) {
                const userGroups = responseRoles.args.rows;
                logger.log('userGroups', userGroups);
                this.$q.store.db.userGroups = userGroups;
            }
        }
        // Запись всех минимальных данных о фермах в хранилище
        if (data.farms && data.farms.rows) {
            const farms = structuredClone(data.farms.rows);
            logger.log('farms', farms);
            this.$q.store.app.farms = farms;
            const farmsByID = {};
            const serversByFarmID = {};
            const serversByID = {};
            farms.forEach((farm, farmKey) => {
                farmsByID[farm.id] = farmKey;
                farm.servers.forEach((server, serverKey) => {
                    serversByFarmID[server.id] = farm.id;
                    serversByID[server.id] = serverKey;
                });
            });
            this.$q.store.app.farmsByID = farmsByID;
            this.$q.store.app.serversByFarmID = serversByFarmID;
            this.$q.store.app.serversByID = serversByID;
        }
        // Обработчики сообщений от ws
        this.$q.class.WS.setListeners();
    }
    /**
     * Действия при логауте
     */
    logout() {
        sessionStorage.removeItem('token');
        this.$q.class.WS.disconnect();
        this.$q.store.app.reset();
        this.clearTokenRefreshTimer();
    }
    async getTelegramLinkList() {
        const response = await this.$q.ws.sendRequest({
            type: 'query',
            iface: 'user',
            method: 'getTelegramLinkList',
            args: {}
        });
        return response?.args || null;
    }
    async createTelegramLinkToken() {
        const response = await this.$q.ws.sendRequest({
            type: 'query',
            iface: 'user',
            method: 'createTelegramLinkToken',
            args: {}
        });
        return response;
    }
    async unlinkTelegramAccount(args) {
        const response = await this.$q.ws.sendRequest({
            type: 'query',
            iface: 'user',
            method: 'unlinkTelegramAccount',
            args
        });
        return response;
    }
    async getAlarmTriggerList() {
        const response = await this.$q.ws.sendRequest({
            type: 'query',
            iface: 'user',
            method: 'getAlarmTriggerList',
            args: {}
        });
        return response?.args || [];
    }
    async setAlarmTrigger(args) {
        const response = await this.$q.ws.sendRequest({
            type: 'query',
            iface: 'user',
            method: 'setAlarmTrigger',
            args
        });
        return response?.args;
    }
    async muteTelegramAlarms(args) {
        const response = await this.$q.ws.sendRequest({
            type: 'query',
            iface: 'user',
            method: 'muteTelegramAlarms',
            args
        });
        return response?.args;
    }
    async getList() {
        const response = await this.$q.ws.sendRequest({
            type: 'query',
            iface: 'user',
            method: 'getList',
            args: {}
        });
        if (response && response.args && response.args.rows) {
            return response.args.rows;
        }
        else {
            return null;
        }
    }
    /**
     * Запуск периодического обновления токена (debug: каждые 60 секунд)
     */
    startTokenRefresh(token) {
        this.clearTokenRefreshTimer();
        if (!token)
            return;
        this.tokenRefreshTimer = setInterval(() => {
            this.refreshToken();
        }, TOKEN_REFRESH_INTERVAL_MS);
    }
    /**
     * Остановка таймера обновления токена
     */
    clearTokenRefreshTimer() {
        if (this.tokenRefreshTimer) {
            clearInterval(this.tokenRefreshTimer);
            this.tokenRefreshTimer = null;
        }
    }
    /**
     * Обновление токена пользователя
     */
    async refreshToken() {
        const token = this.$q.store?.app?.user?.token || sessionStorage.getItem('token');
        if (!token || !this.$q.ws)
            return;
        try {
            const response = await this.$q.ws.sendRequest({
                type: 'query',
                iface: 'user',
                method: 'refreshToken',
                args: { token }
            });
            if (response && response.args && response.args.token) {
                const newToken = response.args.token;
                const tokenExpire = response.args.token_expire;
                if (this.$q.store.app.user) {
                    this.$q.store.app.user = {
                        ...this.$q.store.app.user,
                        token: newToken,
                        token_expire: tokenExpire ?? this.$q.store.app.user.token_expire
                    };
                }
                sessionStorage.setItem('token', newToken);
                logger.log('Token refreshed');
            }
            else if (response && response.args && response.args.message) {
                logger.warn('Token refresh failed:', response.args.message);
            }
        }
        catch (e) {
            logger.warn('Token refresh error:', e);
        }
    }
    createCluster(frontUrl, clusterType, settings) {
        return this.$q.ws.sendRequest({
            type: 'query',
            iface: 'user',
            method: 'createCluster',
            args: {
                front_url: frontUrl,
                cluster_type: clusterType,
                settings
            }
        });
    }
    listClusters(search, offset, limit, order) {
        return this.$q.ws.sendRequest({
            type: 'query',
            iface: 'user',
            method: 'listClusters',
            args: {
                search,
                offset,
                limit,
                order
            }
        });
    }
    updateCluster(clusterId, frontUrl, clusterType, settings) {
        return this.$q.ws.sendRequest({
            type: 'query',
            iface: 'user',
            method: 'updateCluster',
            args: {
                id: clusterId,
                front_url: frontUrl,
                cluster_type: clusterType,
                settings
            }
        });
    }
    deleteCluster(clusterId) {
        return this.$q.ws.sendRequest({
            type: 'query',
            iface: 'user',
            method: 'deleteCluster',
            args: {
                id: clusterId
            }
        });
    }
    updateUser(user) {
        return this.$q.ws.sendRequest({
            type: 'query',
            iface: 'user',
            method: 'update',
            args: {
                ...user
            }
        });
    }
}
export default UserClass;
